import { ContentPage } from './content-page.model';

export const ABOUT_PAGE: ContentPage = {
  eyebrow: 'ABOUT GIVESANTA',
  title: 'We create Christmas magic.\nOne story at a time.',
  intro: [
    'GiveSanta turns the little things that make someone special — their name, photos, interests, achievements and memories — into a personalized cinematic Santa adventure made just for them.',
    'This isn’t simply a message from Santa.',
    'It’s a Christmas movie where someone you love becomes part of the story.',
  ],
  sections: [
    {
      heading: 'MAKE IT FEEL REAL',
      paragraphs: [
        'Christmas magic lives in the details.',
        'When Santa knows their name.',
        'When he remembers something they’re proud of.',
        'When a familiar photo suddenly appears inside his world.',
        'Those little moments create a very big feeling:',
        '“Santa knows me.”',
        'That’s what GiveSanta was created to make possible.',
      ],
    },
    {
      heading: 'A STORY MADE PERSONAL',
      paragraphs: [
        'GiveSanta combines cinematic storytelling, premium production and smart personalization to create a Christmas experience that feels personal from beginning to end.',
        'Names, interests, achievements, personal details and photos become part of the adventure — turning a Christmas story into their Christmas story.',
      ],
    },
    {
      heading: 'MADE FOR SOMEONE SPECIAL',
      paragraphs: [
        'Every GiveSanta movie is created on demand and personalized for one special recipient.',
        'It’s not about adding a name to a standard Christmas greeting.',
        'It’s about creating the feeling that Santa made this movie especially for them.',
      ],
    },
    {
      heading: 'OUR GOAL',
      paragraphs: [
        'A little disbelief.\nA big smile.\nAnd one unforgettable question:',
        '“How did Santa know that?”',
        'That’s the moment we’re creating.',
      ],
    },
    {
      heading: 'CORPORATE & OWNERSHIP',
      paragraphs: [
        'GiveSanta.com and its associated intellectual property, media, scripts, characters, branding elements and digital products are owned and managed by:',
        'STRIPE CORPORATION\n1665 Palm Beach Lakes Blvd., Suite 110\nWest Palm Beach, FL 33401\n(561) 713-6100\ninfo@GiveSanta.com',
        'Stripe Corporation is responsible for:',
        '• Technology and customer platform management\n• Production and content delivery\n• Billing and payment processing\n• Customer support\n• Licensing and intellectual property',
      ],
      small: true,
    },
    {
      heading: 'LEGAL',
      paragraphs: [
        '© Stripe Corporation. All rights reserved.',
        'All GiveSanta media, video content, characters, names, scripts, branding elements and visual assets are proprietary and may not be reproduced or duplicated without written permission.',
      ],
      small: true,
    },
  ],
};

export const CONTACT_PAGE: ContentPage = {
  title: 'Contact',
  intro: [
    'Have a question about GiveSanta, your order, or your personalized movie?',
    'We’re here to help.',
    'Questions, comments and suggestions are always welcome.',
  ],
  sections: [
    {
      heading: 'EMAIL',
      paragraphs: ['info@GiveSanta.com'],
    },
    {
      heading: 'PHONE',
      paragraphs: ['Customer Service:\n\n(561) 713-6100'],
    },
    {
      heading: 'ADDRESS',
      paragraphs: [
        'GiveSanta.com\n1665 Palm Beach Lakes Blvd., Suite 110\nWest Palm Beach, FL 33401\nUnited States',
      ],
    },
    {
      heading: 'ORDER SUPPORT',
      paragraphs: [
        'For questions about an existing order, please include the email address used to place your order and any relevant order information so our team can help you as quickly as possible.',
      ],
    },
  ],
  ctaClass: 'mt-8 inline-flex',
};

export const FAQ_PAGE: ContentPage = {
  title: 'FAQ',
  lead: 'Everything you need to know about creating your personalized GiveSanta movie.',
  sections: [
    {
      heading: 'ORDERING & PERSONALIZATION',
      questions: [
        {
          question: 'How does GiveSanta work?',
          answer: [
            'Tell Santa’s team a few things about the person receiving the movie. We use those details to create a personalized Christmas adventure and send the finished movie to you by email.',
          ],
        },
        {
          question: 'What information will I need?',
          answer: [
            'We’ll ask for details such as the recipient’s name, age, hair color, eye color, a proud moment, an achievement, hobbies or interests, who the movie is from, and a special gift.',
            'Some information is optional.',
          ],
        },
        {
          question:
            'What if I can’t find the right answer in the available options?',
          answer: [
            'No problem. Where available, you can choose to add your own comment instead of selecting one from the list.',
          ],
        },
        {
          question: 'Do I have to complete every field?',
          answer: [
            'No. Some personalization options are optional. You can skip them and still create a magical personalized movie.',
          ],
        },
        {
          question: 'Can I order more than one movie?',
          answer: [
            'Absolutely. You can create a separate personalized GiveSanta movie for each recipient.',
          ],
        },
        {
          question: 'Can I include more than one child in one movie?',
          answer: [
            'Each GiveSanta movie is created for one recipient so the experience can feel truly personal.',
            'If you would like movies for several children, simply create a separate movie for each of them.',
          ],
        },
        {
          question: 'Can I create a movie for a teenager or an adult?',
          answer: [
            'Absolutely. GiveSanta isn’t only for little kids. A personalized Santa movie can also be a fun, surprising and heartwarming Christmas gift for teens and adults.',
          ],
        },
      ],
    },
    {
      heading: 'PHOTOS & PERSONALIZATION',
      questions: [
        {
          question: 'Can I add my own photos?',
          answer: [
            'Yes. You can upload personal photos that may appear throughout the movie, making the experience even more special.',
          ],
        },
        {
          question: 'What kind of photos can I add?',
          answer: [
            'Depending on your personalization choices, you may upload:',
            '• A photo of the recipient\n• A proud moment or achievement\n• A hobby or activity\n• An interest\n• A photo of the person the movie is from\n• A special gift',
          ],
        },
        {
          question: 'Do I need to upload all of the photos?',
          answer: [
            'No. Photos are optional. If you don’t have a particular photo, simply skip that step.',
          ],
        },
      ],
    },
    {
      heading: 'THE MOVIE',
      questions: [
        {
          question: 'How long is the GiveSanta movie?',
          answer: [
            'GiveSanta movies are approximately 9–10 minutes long, depending on the story and personalization.',
          ],
        },
        {
          question: 'Is this just a personalized message from Santa?',
          answer: [
            'No — and that’s what makes GiveSanta different.',
            'GiveSanta is a personalized Christmas adventure where the recipient becomes part of the story.',
            'Their name, interests, achievements, personal details and photos can appear throughout the movie.',
          ],
        },
        {
          question: 'Can I watch a sample before ordering?',
          answer: [
            'Yes. You can watch a video on our website to get a feel for the story, production quality and personalization before creating your own movie.',
          ],
        },
        {
          question: 'What languages are available?',
          answer: [
            'GiveSanta movies are currently available in English and Spanish.',
          ],
        },
        {
          question: 'Are captions available?',
          answer: [
            'Yes. English movies can include English captions, and Spanish movies can include Spanish captions.',
          ],
        },
        {
          question: 'Can I watch the movie more than once?',
          answer: [
            'Absolutely. Your GiveSanta movie can be enjoyed again and again.',
          ],
        },
        {
          question: 'Can I download my movie?',
          answer: [
            'Yes. Once your movie is ready, you’ll receive access to your finished movie and will be able to download and save it.',
          ],
        },
        {
          question: 'Is the movie safe for children to watch?',
          answer: [
            'Yes. GiveSanta movies are designed to be family-friendly and created with children and families in mind.',
          ],
        },
      ],
    },
    {
      heading: 'DELIVERY & TIMING',
      questions: [
        {
          question: 'How quickly will I receive my movie?',
          answer: [
            'Most GiveSanta movies are ready within approximately 3 hours.',
            'During especially busy holiday periods, delivery may take up to 24 hours.',
          ],
        },
        {
          question: 'How will I receive my movie?',
          answer: [
            'We’ll send you an email as soon as your personalized GiveSanta movie is ready.',
          ],
        },
        {
          question: 'Who receives the movie?',
          answer: [
            'The person placing the order receives the finished movie first, so you can choose exactly when and how to share the surprise.',
          ],
        },
        {
          question: 'Is the movie streamed or do I receive a file?',
          answer: [
            'You’ll receive access to your finished movie and will be able to download and save it.',
          ],
        },
      ],
    },
    {
      heading: 'SUPPORT & POLICIES',
      questions: [
        {
          question: 'What if I make a mistake when entering the information?',
          answer: [
            'Because every GiveSanta movie is individually created, changes are only possible before production begins.',
            'Once production has started, the order cannot be changed.',
          ],
        },
        {
          question: 'Can I change my personalization after ordering?',
          answer: [
            'Because every GiveSanta movie is individually created, changes are only possible before production begins.',
            'Once production has started, the order cannot be changed.',
          ],
        },
        {
          question: 'Can I cancel my order?',
          answer: [
            'Because every GiveSanta movie is individually created, cancellations are only possible before production begins.',
            'Once production has started, the order cannot be cancelled.',
          ],
        },
        {
          question: 'What if there is a problem with my movie?',
          answer: [
            'Please contact our support team and tell us what happened.',
            'We’ll review the issue and help you find the best solution.',
          ],
        },
      ],
    },
  ],
  ctaClass: 'mt-12 inline-flex',
};

export const PRIVACY_PAGE: ContentPage = {
  title: 'Privacy Policy',
  lastUpdated: 'Last Updated: October 8, 2026',
  introSection: {
    heading: 'INTRODUCTION',
    paragraphs: [
      'Your privacy matters to us.',
      'This Privacy Policy explains how GiveSanta collects, uses, stores and shares information when you visit GiveSanta.com, create a personalized movie, upload photos, make a purchase or contact us.',
      'GiveSanta.com is owned and operated by:',
      'Stripe Corporation\n1665 Palm Beach Lakes Blvd., Suite 110\nWest Palm Beach, FL 33401\nUnited States',
      'Email: info@GiveSanta.com\nCustomer Service: (561) 713-6100',
    ],
  },
  sections: [
    {
      heading: '1. INFORMATION WE COLLECT',
      paragraphs: [
        'When you use GiveSanta, we may collect information that you provide directly to us.',
        'This may include:',
        '• Your name\n• Your email address\n• Order and transaction information\n• Information provided to personalize a GiveSanta movie\n• The recipient’s first name\n• Age\n• Hair color\n• Eye color\n• Hobbies, activities and interests\n• Proud moments or achievements\n• Gift information\n• Information about who the movie is from\n• Custom comments you choose to provide\n• Photos you choose to upload\n• Communications you send to our customer support team',
        'Some personalization fields and photo uploads may be optional.',
      ],
    },
    {
      heading: '2. INFORMATION ABOUT CHILDREN',
      paragraphs: [
        'GiveSanta movies may be purchased by adults for children.',
        'GiveSanta is intended to be used by adults purchasing and creating personalized movies for recipients.',
        'Children should not place orders, create accounts, make purchases or submit personal information or photographs directly through GiveSanta without the involvement of a parent or legal guardian.',
        'If you provide information or photographs relating to a child, you confirm that you are authorized to provide that information for the purpose of creating the personalized movie.',
        'If you believe information relating to a child has been submitted without appropriate authorization, please contact us at:',
        'info@GiveSanta.com',
      ],
    },
    {
      heading: '3. HOW WE USE INFORMATION',
      paragraphs: [
        'We may use information provided through GiveSanta to:',
        '• Create and personalize your GiveSanta movie\n• Process and manage your order\n• Deliver your completed movie\n• Provide customer support\n• Correct personalization or technical issues\n• Process payments\n• Maintain and improve GiveSanta\n• Protect the security and integrity of our website and services\n• Comply with applicable legal obligations',
        'We do not use personal photos submitted for an order to advertise GiveSanta without separate permission.',
      ],
    },
    {
      heading: '4. PHOTOS AND PERSONAL CONTENT',
      paragraphs: [
        'Photos and personal information you upload are used to create and deliver the personalized movie you requested.',
        'You retain ownership of the personal photos and personal content you submit.\nBy uploading content, you give GiveSanta and Stripe Corporation the limited permission necessary to process that content for the purpose of creating, delivering and supporting your personalized movie.',
        'You are responsible for ensuring that you have the right or appropriate permission to upload the photos and information you provide.',
      ],
    },
    {
      heading: '5. PAYMENT INFORMATION',
      paragraphs: [
        'Payments may be processed through third-party payment service providers.',
        'Payment card information may be collected and processed directly by the applicable payment provider rather than stored directly by GiveSanta.',
        'Those providers may process information according to their own privacy policies and legal obligations.',
      ],
    },
    {
      heading: '6. AUTOMATICALLY COLLECTED INFORMATION',
      paragraphs: [
        'When you visit GiveSanta.com, certain technical information may be collected automatically.',
        'This may include:',
        '• IP address\n• Browser type\n• Device type\n• Operating system\n• Pages visited\n• Approximate location derived from technical information\n• Website interactions\n• Referral information\n• Cookies and similar technologies',
        'This information may be used to operate, secure, understand and improve the website.',
      ],
    },
    {
      heading: '7. COOKIES AND SIMILAR TECHNOLOGIES',
      paragraphs: [
        'GiveSanta may use cookies and similar technologies that are necessary for website functionality, security, analytics and user experience.',
        'Where required by applicable law, users will be provided with appropriate choices regarding non-essential cookies.',
      ],
    },
    {
      heading: '8. SERVICE PROVIDERS',
      paragraphs: [
        'We may use trusted third-party service providers to help operate GiveSanta.',
        'These may include providers supporting:',
        '• Website hosting\n• Cloud infrastructure\n• Movie production and processing\n• Email delivery\n• Payment processing\n• Analytics\n• Security\n• Customer support',
        'These providers may access information only as necessary to perform services on our behalf and subject to applicable contractual and legal requirements.',
      ],
    },
    {
      heading: '9. HOW WE SHARE INFORMATION',
      paragraphs: [
        'We do not sell personal photos or personalization information.',
        'We may share information with service providers where necessary to operate GiveSanta, process an order, create and deliver a movie, process payment, provide support or comply with legal requirements.',
        'We may also disclose information when required by law or when reasonably necessary to protect the rights, security or integrity of GiveSanta, our customers or others.',
      ],
    },
    {
      heading: '10. DATA RETENTION',
      paragraphs: [
        'We retain personal information only for as long as reasonably necessary for the purposes described in this Privacy Policy, including providing the service, supporting orders, maintaining appropriate business records and complying with legal obligations.',
        'Different categories of information may be retained for different periods.',
        'Where personal information is no longer reasonably required, we may delete or anonymize it in accordance with our applicable retention practices.',
      ],
    },
    {
      heading: '11. DATA SECURITY',
      paragraphs: [
        'We use reasonable administrative, technical and organizational measures designed to protect personal information.',
        'However, no online service or method of electronic storage can guarantee absolute security.',
      ],
    },
    {
      heading: '12. YOUR PRIVACY RIGHTS',
      paragraphs: [
        'Depending on where you live, applicable law may provide you with rights relating to your personal information.\nThese may include rights to request access, correction or deletion of certain personal information, or other rights provided by applicable privacy law.',
        'To make a privacy request, contact:',
        'info@GiveSanta.com',
        'We may need to verify your identity before completing certain requests.',
      ],
    },
    {
      heading: '13. THIRD-PARTY SERVICES',
      paragraphs: [
        'GiveSanta may use or link to services operated by third parties.',
        'Their handling of information may be governed by their own privacy policies.',
        'We encourage users to review the privacy practices of services they choose to use.',
      ],
    },
    {
      heading: '14. CHANGES TO THIS PRIVACY POLICY',
      paragraphs: [
        'We may update this Privacy Policy from time to time.',
        'When we do, we will update the “Last Updated” date displayed at the top of this page.',
      ],
    },
    {
      heading: '15. CONTACT US',
      paragraphs: [
        'For privacy questions or requests, contact:',
        'GiveSanta.com\nStripe Corporation\n1665 Palm Beach Lakes Blvd., Suite 110\nWest Palm Beach, FL 33401\nUnited States',
        'Email: info@GiveSanta.com\nCustomer Service: (561) 713-6100',
      ],
    },
  ],
};

export const TERMS_PAGE: ContentPage = {
  title: 'Terms & Conditions',
  lastUpdated: 'Last Updated: October 8, 2026',
  introSection: {
    heading: 'INTRODUCTION',
    paragraphs: [
      'Welcome to GiveSanta.',
      'These Terms & Conditions govern your use of GiveSanta.com and your purchase of personalized GiveSanta movies.',
      'By using our website or placing an order, you agree to these Terms.',
      'GiveSanta.com is owned and operated by:',
      'Stripe Corporation\n1665 Palm Beach Lakes Blvd., Suite 110\nWest Palm Beach, FL 33401\nUnited States',
      'Email: info@GiveSanta.com\nCustomer Service: (561) 713-6100',
    ],
  },
  sections: [
    {
      heading: '1. ABOUT GIVESANTA',
      paragraphs: [
        'GiveSanta creates personalized digital Christmas movies using information, selections and optional photos provided by the customer.',
        'Each movie is created specifically for the selected recipient and may incorporate details such as their name, age, appearance, interests, achievements, special moments, photos and gift information.',
        'The final movie is a digital product delivered electronically.',
      ],
    },
    {
      heading: '2. PLACING AN ORDER',
      paragraphs: [
        'When placing an order, you are responsible for providing accurate information.',
        'Please review names, personalization details, selections, email addresses and uploaded photos carefully before submitting your order.',
        'Once production has started, we may not be able to change the information used to create your movie.',
      ],
    },
    {
      heading: '3. PERSONALIZED CONTENT',
      paragraphs: [
        'GiveSanta movies are created using the information you provide.',
        'Because each movie is personalized, the final result may vary depending on the information, selections and photos submitted.',
        'Personalization is designed to be incorporated naturally into the GiveSanta story and does not mean that every submitted detail or photo will necessarily appear in the final movie.',
      ],
    },
    {
      heading: '4. PHOTOS AND CONTENT YOU PROVIDE',
      paragraphs: [
        'By uploading a photo, text or other content to GiveSanta, you confirm that:',
        '• You have the right to provide and use that content.\n• You have any necessary permission to provide photographs of other people.\n• The content does not violate another person’s rights.\n• The content is appropriate for use in a family-friendly personalized Christmas movie.',
        'You retain ownership of the personal photos and personal content you submit.',
        'You grant GiveSanta and Stripe Corporation the limited rights necessary to process your submitted content for the purpose of creating, delivering and supporting your personalized movie.',
      ],
    },
    {
      heading: '5. CHILDREN’S INFORMATION',
      paragraphs: [
        'GiveSanta movies may be personalized for children.',
        'The person placing the order is responsible for ensuring that they are authorized to provide any information or photographs relating to a child.',
        'Children should not place orders or submit personal information directly through GiveSanta without the involvement of a parent or legal guardian.',
      ],
    },
    {
      heading: '6. DELIVERY',
      paragraphs: [
        'GiveSanta movies are delivered digitally.',
        'Most movies are expected to be ready within approximately 3 hours.',
        'During periods of high demand, including the Christmas season, delivery may take up to 24 hours.',
        'Delivery times are estimates and are not guaranteed.',
        'Customers are responsible for providing a valid email address and checking their inbox and spam or junk folders for delivery notifications.',
      ],
    },
    {
      heading: '7. CHANGES AND CANCELLATIONS',
      paragraphs: [
        'If you notice an error after placing your order, contact us as soon as possible at:',
        'info@GiveSanta.com',
        'If production has not yet started, we will do our best to update your personalization information.',
        'Because each GiveSanta movie is individually created, orders generally cannot be changed or cancelled once production has begun.',
      ],
    },
    {
      heading: '8. REFUNDS AND TECHNICAL PROBLEMS',
      paragraphs: [
        'Because GiveSanta movies are personalized digital products created specifically for each recipient, purchases are generally non-refundable once production has started.',
        'However, if your movie contains a technical problem, is not delivered correctly, or contains an error caused by GiveSanta, please contact us.',
        'We will review the issue and, where appropriate, correct or replace the movie.',
        'Nothing in these Terms limits any rights you may have under applicable consumer protection law.',
      ],
    },
    {
      heading: '9. PERSONALIZATION EXPECTATIONS',
      paragraphs: [
        'GiveSanta is an entertainment experience.',
        'We work to make each movie feel personal and magical, but individual reactions to personalized content will naturally vary.',
        'Descriptions, previews and sample movies are provided to help customers understand the general style and experience of GiveSanta.',
        'Your personalized movie will differ from samples because it is created using your selected information and content.',
      ],
    },
    {
      heading: '10. PERSONAL USE',
      paragraphs: [
        'Your GiveSanta movie is provided for personal, non-commercial use unless otherwise agreed in writing.',
        'You may download, save and share your personalized movie with family and friends.',
        'You may not sell, commercially distribute, reproduce or commercially exploit GiveSanta movies, characters, scripts, branding or other proprietary content without written permission.',
      ],
    },
    {
      heading: '11. INTELLECTUAL PROPERTY',
      paragraphs: [
        'The GiveSanta website, brand, stories, scripts, characters, video footage, graphics, designs, technology and other original GiveSanta materials are owned by or licensed to Stripe Corporation and are protected by applicable intellectual property laws.',
        'The personal photos and personal content you submit remain yours.',
        'Purchasing a GiveSanta movie does not transfer ownership of GiveSanta intellectual property to you.',
      ],
    },
    {
      heading: '12. ACCEPTABLE USE',
      paragraphs: [
        'You may not use GiveSanta to submit content that is unlawful, threatening, abusive, discriminatory, sexually explicit, exploitative, harmful or otherwise inappropriate.',
        'We reserve the right to reject content or orders that violate these standards.',
      ],
    },
    {
      heading: '13. WEBSITE AVAILABILITY',
      paragraphs: [
        'We work to keep GiveSanta available and operating reliably, particularly during the holiday season.',
        'However, temporary interruptions may occur because of maintenance, technical issues, internet services or circumstances outside our reasonable control.',
      ],
    },
    {
      heading: '14. LIMITATION OF LIABILITY',
      paragraphs: [
        'To the fullest extent permitted by applicable law, Stripe Corporation will not be liable for indirect, incidental, special or consequential damages arising from the use of GiveSanta or a personalized movie.',
        'Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited.',
      ],
    },
    {
      heading: '15. GOVERNING LAW',
      paragraphs: [
        'These Terms are governed by the laws of the State of Florida, without regard to conflict-of-law principles.',
        'Any rights available to consumers under applicable law remain unaffected.',
      ],
    },
    {
      heading: '16. CHANGES TO THESE TERMS',
      paragraphs: [
        'We may update these Terms from time to time.',
        'The current version and its “Last Updated” date will be available on GiveSanta.com.',
      ],
    },
    {
      heading: '17. CONTACT',
      paragraphs: [
        'Questions about these Terms or your GiveSanta order can be sent to:',
        'GiveSanta.com\nStripe Corporation\n1665 Palm Beach Lakes Blvd., Suite 110\nWest Palm Beach, FL 33401\nUnited States',
        'Email: info@GiveSanta.com\nCustomer Service: (561) 713-6100',
      ],
    },
  ],
};
